import { useId } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import type {
  PublicServiceStatus,
  PublicSparklineKind,
} from "@/v2-status-client/status/status.types"
import { buildSparklineGeometry } from "@/v2-status-client/status/sparkline/status.sparkline.utils"
import { formatLatencyMs } from "@/v2-status-client/status/status.utils"

export type StatusSparklineProps = {
  // Last-24h hourly avg latency in ms, oldest-first. Nulls render
  // as gaps. Same shape the public API emits.
  values: ReadonlyArray<number | null>
  status: PublicServiceStatus
  // Ms above which we'd consider the service "slow" (typically the
  // 90-day p95). Drawn as an amber dashed reference line so the
  // strip itself answers "is this slower than baseline?" without a
  // tooltip. Hidden when null. Mirrors the operator dashboard's
  // big response-time chart -- same colour, same intent -- so an
  // operator and a customer reading the same row see the same
  // signal.
  baselineMs?: number | null
  // What kind of data populated `values`. Drives two presentation
  // hints when the data is the synthesised baseline (rather than
  // live hourly samples):
  //
  //   - the trace renders dashed instead of solid, so a viewer
  //     can tell at a glance that they're looking at a typical
  //     response-time line rather than the literal last 24 hours.
  //   - the area fill is suppressed, since "filled in below the
  //     line" implies measurements over time and we don't have any.
  //
  // Defaults to "live" when omitted so older API responses (and
  // the demo fixture) keep their existing rendering.
  kind?: PublicSparklineKind
  className?: string
  title?: string
}

// Inline-SVG latency sparkline. We deliberately avoid recharts/nivo
// here -- the public bundle doesn't need a 200 KB chart library
// just to draw 24 points in a 100x32 box. The geometry helper does
// the math; this component just wires the path into the SVG and
// picks a tone that matches the row's status.
const TONE: Record<PublicServiceStatus, { stroke: string; fill: string }> = {
  operational: {
    stroke: "stroke-emerald-400",
    fill: "fill-emerald-400/15",
  },
  degraded: {
    stroke: "stroke-amber-400",
    fill: "fill-amber-400/15",
  },
  down: {
    stroke: "stroke-red-400",
    fill: "fill-red-400/15",
  },
}

const WIDTH = 120
const HEIGHT = 32

function StatusSparkline(props: Readonly<StatusSparklineProps>) {
  const { values, status, baselineMs, kind = "live", className, title } = props
  const tone = TONE[status]
  const isBaselineFallback = kind === "baseline"
  // No useMemo here on purpose: `values` is a fresh reference on
  // every poll (the JSON payload is reparsed), so memoisation
  // would never hit. The geometry math is pure arithmetic over
  // 24 numbers and runs in microseconds.
  const geometry = buildSparklineGeometry(values, {
    width: WIDTH,
    height: HEIGHT,
    baseline: baselineMs,
  })
  const gradientId = useId()

  if (!geometry.hasData) {
    // No 24h hourly samples yet (a freshly-onboarded service or
    // one whose probes only just started). Render as a faint
    // baseline rule rather than a dashed-bordered "no data" box
    // -- the row already shows the headline avg/p95 numbers, so
    // the sparkline slot just needs to occupy the space without
    // shouting "broken UI".
    return (
      <svg
        width={WIDTH}
        height={HEIGHT}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className={cn("block opacity-60", className)}
        role="img"
        aria-label={title ?? "No latency samples in the last 24h"}
      >
        <line
          x1={0}
          x2={WIDTH}
          y1={HEIGHT / 2}
          y2={HEIGHT / 2}
          className="stroke-border"
          strokeDasharray="2 4"
          strokeWidth={1}
        />
      </svg>
    )
  }

  // Aria label folds the data source into the announced text so a
  // screen reader user gets the same "this is the typical response
  // time, not the live last-24h" cue the dashed trace conveys
  // visually.
  const windowLabel = isBaselineFallback ? "typical response time" : "last 24h"
  const ariaLabel =
    title ??
    (geometry.baselineY != null && baselineMs != null
      ? `Latency, ${windowLabel}. Baseline ${formatLatencyMs(baselineMs)}.`
      : `Latency, ${windowLabel}`)

  return (
    <svg
      width={WIDTH}
      height={HEIGHT}
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      className={cn("block overflow-visible", className)}
      role="img"
      aria-label={ariaLabel}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity={0.45} />
          <stop offset="100%" stopColor="currentColor" stopOpacity={0.02} />
        </linearGradient>
      </defs>
      {/*
        Suppress the area fill when we're showing the synthesised
        baseline strip -- a filled area implies "here's how the
        line moved over time", which is exactly what we don't
        have. The dashed line alone communicates "typical".
      */}
      {!isBaselineFallback && (
        <path d={geometry.areaPath} className={cn(tone.fill, "text-current")} />
      )}
      {/*
        Baseline rendered between the area fill and the line so
        it's visible against the tinted area but never obscures
        the actual data trace. Amber + dashed mirrors the
        operator dashboard's p95 reference line: same colour,
        same semantics ("above this is the slow tail"). We draw
        it in raw SVG colour rather than a Tailwind class so the
        amber survives even if the surrounding tone changes the
        currentColor inheritance chain.
      */}
      {geometry.baselineY != null && (
        <line
          x1={0}
          x2={WIDTH}
          y1={geometry.baselineY}
          y2={geometry.baselineY}
          stroke="rgb(217 119 6)"
          strokeOpacity={0.85}
          strokeDasharray="4 3"
          strokeWidth={1}
          shapeRendering="crispEdges"
        />
      )}
      <path
        d={geometry.linePath}
        fill="none"
        strokeWidth={1.4}
        strokeLinecap="round"
        strokeLinejoin="round"
        // Dashed pattern when the strip is a synthesised baseline
        // rather than live hourly samples. Same line, slightly
        // weaker presence -- the row is still readable, but the
        // viewer is signalled "this isn't the literal last 24h".
        strokeDasharray={isBaselineFallback ? "3 2" : undefined}
        className={cn(tone.stroke, isBaselineFallback && "opacity-60")}
      />
    </svg>
  )
}

export default StatusSparkline
